import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { AppState } from 'src/app/root-store/state';
import { Observable, Subject, Subscription, takeUntil } from 'rxjs';
import {
  selectJobseekerPersonalInfo,
  selectJobseeker,
  selectJobseekerLanguages,
  selectTotalWorkExperienceMonths,
  selectTotalWorkExperienceYears,
  selectMaxEducationLevel,
  selectJobseekerWorkExperiences,
  languageInModeration,
  documentsInModeration,
  selectJobseekerDocuments,
  selectEducations,
  selectIsProfileLoading,
  selectIsJobseekerProfileUpdating,
  selectIsCvExportLoading,
  selectPhotoIsInModeration,
  selectDocumentTypeListMap,
  selectJobseekerModerationLastChangeDate,
  selectIsWorkExperienceFilledIsInModeration,
  selectPendingModifiedJobseekerWorkExperiences,
  selectPendingNewJobseekerWorkExperiences,
  selectHardSkills,
  selectSoftSkills,
  hardSkillsIsInModeration,
  softSkillsIsInModeration,
} from 'src/app/root-store/jobseeker-store/jobseeker-profile/selectors';
import { IconButtonTypeEnum } from '@hrra/ui';
// import { JobseekerEducationViewModel } from '../shared/models/jobseeker-education-model';
import {
  Jobseeker,
  JobseekerDocument,
  JobseekerEducationViewModel,
  JobseekerLanguage,
  JobseekerSkillItem,
  JobseekerWorkExperience,
  JobseekerWorkExperienceViewModel,
  NecessaryField,
  SkillBaseTypeEnum,
  UserTypeEnum,
} from '@hrra/common-models';
import { JobseekerDialogService } from '../shared/services/jobseeker-dialog.service';
import {
  deleteJobseekerDocument,
  deleteJobseekerEducation,
  deleteJobseekerLanguage,
  deleteJobseekerPhoto,
  deleteJobseekerWorkExperience,
  downloadDocumentAttachment,
  downloadEducationAttachment,
  downloadLanguageAttachment,
  downloadWorkExperienceAttachment,
  jobseekerCvExport,
  loadJobseekerProfile,
  removeSavedJobseekerSkill,
  reorderSavedJobseekerSkills,
  setIsWorkExperienceFilled,
} from 'src/app/root-store/jobseeker-store/jobseeker-profile/actions';
import { MetaService } from 'src/app/shared/services/meta.service';
import {
  selectCurrentUserType,
  selectCurrentLanguage,
  selectCurrentWebsiteNameForSeo,
  selectTimeZone,
  selectSystemConfigs,
  selectJobseekerPortalPopupEnabled,
} from 'src/app/root-store/selectors';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { MatDialog } from '@angular/material/dialog';
import { JobseekerPersonalInfoEditComponent } from '../jobseeker-personal-info-edit/jobseeker-personal-info-edit.component';
import { loadIndustries } from 'src/app/root-store/jobseeker-store/industry-picker/actions';
import {
  getJobseekerIndustryList,
  getJobseekerIndustryListFail,
} from 'src/app/root-store/jobseeker-store/jobseeker-industry/actions';
import { AuthService } from 'src/app/core/auth.service';
import { InfoDialogService } from '@hrra/ui';
import { getJobseekerSpecializationList } from 'src/app/root-store/jobseeker-store/jobseeker-specialization/actions';

@Component({
    selector: 'app-jobseeker-profile',
    templateUrl: './redesign/jobseeker-profile.component.html',
    styleUrls: ['./redesign/jobseeker-profile.component.scss'],
    standalone: false
})
export class JobseekerProfileComponent implements OnInit, OnDestroy {
  metaDataSubscription: Subscription;

  private destroy$ = new Subject<void>();

  jobseeker$: Observable<Jobseeker>;
  jobseekerPersonalInfo$: Observable<any>;
  jobseekerWorkExperiences$: Observable<JobseekerWorkExperienceViewModel[]>;
  jobseekerEducations$: Observable<JobseekerEducationViewModel[]>;
  jobseekerLanguages$: Observable<JobseekerLanguage[]>;
  jobseekerHardSkills$: Observable<JobseekerSkillItem[]>;
  jobseekerSoftSkills$: Observable<JobseekerSkillItem[]>;
  workExperienceYears$: Observable<any>;
  workExperienceMonth$: Observable<any>;
  educationMaxLevel$: Observable<string>;
  documents$: Observable<JobseekerDocument[]>;

  jobseekerPendingExperiences$: Observable<JobseekerWorkExperience[]>;
  jobseekerNewExperiences$: Observable<JobseekerWorkExperience[]>;
  isPopupEnabled$: Observable<boolean>;
  languagesInMod$: Observable<{ [index: number]: boolean }>;
  jobseekerEducationInMod$: Observable<{ [index: number]: boolean }>;
  documentInMod$: Observable<{ [index: number]: boolean }>;

  isLoading$: Observable<boolean>;
  cvExportLoading$: Observable<boolean>;

  timeZone$: Observable<string>;
  photoIsInModeration$: Observable<boolean>;
  documentTypeMap$: Observable<any>;
  currentUserType$: Observable<UserTypeEnum>;
  currentLanguage$: Observable<string>;

  isWorkExperienceFilledInModeration$: Observable<boolean>;
  hardSkillsInModeration$: Observable<boolean>;
  softSkillsInModeration$: Observable<boolean>;

  public iconButtonType: any;
  public necessaryField: any;
  public skillBase: any;

  public necessaryFieldMap: { [index: number]: string };

  // the template is shared with jobseeker-profile-for-admin, which has no CV export
  public readonly showCvExport = true;

  constructor(
    private store: Store<AppState>,
    private jobseekerDialogService: JobseekerDialogService,
    private dialogService: DialogService,
    private auth: AuthService,
    private infoPopup: InfoDialogService,
    private dialog: MatDialog,
    private metaService: MetaService
  ) {
    (this.iconButtonType = IconButtonTypeEnum);
      (this.necessaryField = NecessaryField);
      (this.skillBase = SkillBaseTypeEnum);
  }

  ngOnInit() {
    this.store.dispatch(loadJobseekerProfile());

    this.store.dispatch(getJobseekerIndustryList());

    this.isPopupEnabled$ = this.store.pipe(
      select(selectJobseekerPortalPopupEnabled)
    );

    this.isPopupEnabled$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isPopupEnabled) => {
        if (isPopupEnabled) {
          const isLarge = false;
          if (this.auth.isJobseekerSignedIn()) {
            const userId = this.auth.getUserId();
            this.infoPopup.tryShow(userId, isLarge);
          }
        }
    });

    this.store.dispatch(getJobseekerSpecializationList());

    this.jobseeker$ = this.store.pipe(select(selectJobseeker));
    this.jobseekerPersonalInfo$ = this.store.pipe(
      select(selectJobseekerPersonalInfo)
    );
    this.jobseekerWorkExperiences$ = this.store.pipe(
      select(selectJobseekerWorkExperiences)
    );
    this.jobseekerEducations$ = this.store.pipe(select(selectEducations));
    this.jobseekerLanguages$ = this.store.pipe(
      select(selectJobseekerLanguages)
    );
    this.jobseekerHardSkills$ = this.store.pipe(select(selectHardSkills));
    this.jobseekerSoftSkills$ = this.store.pipe(select(selectSoftSkills));
    this.hardSkillsInModeration$ = this.store.pipe(
      select(hardSkillsIsInModeration)
    );
    this.softSkillsInModeration$ = this.store.pipe(
      select(softSkillsIsInModeration)
    );

    this.workExperienceMonth$ = this.store.pipe(
      select(selectTotalWorkExperienceMonths)
    );
    this.workExperienceYears$ = this.store.pipe(
      select(selectTotalWorkExperienceYears)
    );

    this.educationMaxLevel$ = this.store.pipe(select(selectMaxEducationLevel));
    this.documents$ = this.store.pipe(select(selectJobseekerDocuments));

    this.languagesInMod$ = this.store.pipe(select(languageInModeration));
    this.documentInMod$ = this.store.pipe(select(documentsInModeration));

    this.jobseekerNewExperiences$ = this.store.pipe(
      select(selectPendingModifiedJobseekerWorkExperiences)
    );
    this.jobseekerPendingExperiences$ = this.store.pipe(
      select(selectPendingNewJobseekerWorkExperiences)
    );

    this.isLoading$ = this.store.pipe(select(selectIsProfileLoading));
    this.cvExportLoading$ = this.store.pipe(select(selectIsCvExportLoading));

    this.timeZone$ = this.store.pipe(select(selectTimeZone));
    this.photoIsInModeration$ = this.store.pipe(
      select(selectPhotoIsInModeration)
    );
    this.documentTypeMap$ = this.store.pipe(select(selectDocumentTypeListMap));
    this.currentUserType$ = this.store.pipe(select(selectCurrentUserType));
    this.currentLanguage$ = this.store.pipe(select(selectCurrentLanguage));

    this.isWorkExperienceFilledInModeration$ = this.store.pipe(
      select(selectIsWorkExperienceFilledIsInModeration)
    );

    this.metaDataSubscription = this.store
      .pipe(select(selectCurrentWebsiteNameForSeo))
      .subscribe((d) => {
        this.metaService.updateMetaData(d);
      });

    this.necessaryFieldMap = {
      [NecessaryField.Name]: 'label.Name',
      [NecessaryField.Surname]: 'label.Surname',
      [NecessaryField.CV]: 'label.CV',
      [NecessaryField.Phone]: 'label.Phone',
      [NecessaryField.City]: 'label.City',
      [NecessaryField.Gender]: 'label.Gender',
      [NecessaryField.BirthDate]: 'label.DateOfBirth',
      [NecessaryField.WorkExperience]: 'label.WorkExperience',
      [NecessaryField.Education]: 'label.Education',
      [NecessaryField.Language]: 'label.Languages',
    };
  }

  ngOnDestroy() {
    this.metaDataSubscription.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  onEducationCreate() {
    this.jobseekerDialogService.editJobseekerEducation(null, true);
  }

  public isExperienceInModeration(
    newExperiences: JobseekerWorkExperience[],
    modifiedExperiences: JobseekerWorkExperience[],
    experienceId: string
  ) {

    return newExperiences?.some(
        (experience) => experience.experienceId === experienceId
      ) ||
      modifiedExperiences?.some(
        (experience) => experience.experienceId === experienceId
      )
  }

  onLanguageCreate() {
    this.jobseekerDialogService.editJobseekerLanguage(null, true);
  }

  onDocumentCreate() {
    this.jobseekerDialogService.uploadDocument();
  }

  onSkillsEdit(skillBaseType: SkillBaseTypeEnum) {
    this.jobseekerDialogService.editJobseekerSkills(skillBaseType);
  }

  deleteSkill(index: number, skillBaseType: SkillBaseTypeEnum) {
    this.dialogService
      .openConfirmDialog('message.AreYouSure')
      .afterClosed()
      .subscribe((res) => {
        if (res) {
          this.store.dispatch(
            removeSavedJobseekerSkill({ payload: { index: index, skillBaseType: skillBaseType } })
          );
        }
      });
  }

  reorderSkills(event: { previousIndex: number; currentIndex: number }, skillBaseType: SkillBaseTypeEnum) {
    this.store.dispatch(reorderSavedJobseekerSkills({ payload: { ...event, skillBaseType: skillBaseType } }));
  }

  onWorkExperienceCreate() {
    this.jobseekerDialogService.editJobseekerWorkExperience(true, null);
  }

  onIsWorkExperienceFilled(moderationLastChangeDate: Date) {
    this.store.dispatch(
      setIsWorkExperienceFilled({
        payload: {
          isWorkExperienceFilled: true,
          moderationLastChangeDate: moderationLastChangeDate,
        },
      })
    );
  }

  onCvExport() {
    this.store.dispatch(jobseekerCvExport());
  }

  editEducation(objectId: string) {
    this.jobseekerDialogService.editJobseekerEducation(objectId, false);
  }

  deleteEducation(event: any, moderationLastChangeDate: Date) {
    this.dialogService
      .openConfirmDialog('message.AreYouSure')
      .afterClosed()
      .subscribe((res) => {
        if (res) {
          this.store.dispatch(
            deleteJobseekerEducation({
              payload: {
                educationId: event.id,
                moderationLastChangeDate: moderationLastChangeDate,
                objectId: event.objectId,
              },
            })
          );
        }
      });
  }

  downloadEducationAttachment(objectId: string) {
    this.store.dispatch(
      downloadEducationAttachment({
        payload: { jobseekerEducationObjectId: objectId },
      })
    );
  }

  editLanguage(languageIdInfo: { id: number; objectId: string }) {
    this.jobseekerDialogService.editJobseekerLanguage(languageIdInfo, false);
  }

  deleteLanguage(event: any, moderationLastChangeDate: Date) {
    this.dialogService
      .openConfirmDialog('message.AreYouSure')
      .afterClosed()
      .subscribe((res) => {
        if (res) {
          this.store.dispatch(
            deleteJobseekerLanguage({
              payload: {
                languageId: event.id,
                moderationLastChangeDate: moderationLastChangeDate,
                objectId: event.objectId,
              },
            })
          );
        }
      });
  }

  downloadLanguageAttachment(objectId: string) {
    this.store.dispatch(
      downloadLanguageAttachment({
        payload: { jobseekerLanguageObjectId: objectId },
      })
    );
  }

  editWorkExperience(workExperience: JobseekerWorkExperienceViewModel) {
    this.jobseekerDialogService.editJobseekerWorkExperience(
      false,
      workExperience
    );
  }

  deleteExperience(event: any, moderationLastChangeDate: Date) {
    this.dialogService
      .openConfirmDialog('message.AreYouSure')
      .afterClosed()
      .subscribe((res) => {
        if (res) {
          this.store.dispatch(
            deleteJobseekerWorkExperience({
              payload: {
                experienceId: event.id,
                moderationLastChangeDate: moderationLastChangeDate,
                objectId: event.objectId,
              },
            })
          );
        }
      });
  }

  downloadExperienceAttachment(experienceId: string) {
    this.store.dispatch(
      downloadWorkExperienceAttachment({
        payload: { experienceId },
      })
    );
  }

  editPersonalInfo() {
    this.dialog.open(JobseekerPersonalInfoEditComponent, {
      width: '36.25rem',
      maxWidth: 'calc(100vw - 2rem)',
      maxHeight: '92vh',
      panelClass: 'jobseeker-edit-dialog-container',
      backdropClass: 'jobseeker-edit-dialog-backdrop',
      id: 'personalInfo',
    });
  }

  editPhoto(isPhotoInModeration: boolean, photoHref: string) {
    this.jobseekerDialogService.editJobseekerPhoto(
      photoHref,
      isPhotoInModeration
    );
  }

  deletePhoto(moderationLastChangeDate: Date) {
    this.dialogService
      .openConfirmDialog('message.AreYouSure')
      .afterClosed()
      .subscribe((res) => {
        if (res) {
          this.store.dispatch(
            deleteJobseekerPhoto({
              payload: { moderationLastChangeDate: moderationLastChangeDate },
            })
          );
        }
      });
  }

  deleteDocument(event: any, moderationLastChangeDate: Date) {
    this.dialogService
      .openConfirmDialog('message.AreYouSure')
      .afterClosed()
      .subscribe((res) => {
        if (res) {
          this.store.dispatch(
            deleteJobseekerDocument({
              payload: {
                documentId: event.id,
                moderationLastChangeDate: moderationLastChangeDate,
                objectId: event.objectId,
              },
            })
          );
        }
      });
  }

  downloadDocument(objectId: string) {
    this.store.dispatch(
      downloadDocumentAttachment({ payload: { documentObjectId: objectId } })
    );
}
}